/**
 * Subscription plan catalog — single source for sorting, active filter, and slug resolution.
 * Used by dashboard pricing and marketing Pricing.html loader.
 *
 * Keep PLAN_SLUG_TO_DB_NAME in sync with:
 *   llm_server/deployment_package/plan_slug_catalog.py
 */

import { PRICING_HERO } from '../data/pricingCatalog';

/** Dashboard slug -> DB subscription_plans.plan_name */
export const PLAN_SLUG_TO_DB_NAME = {
  pro_plan: 'Professional',
  developer_plus: 'Developer Plus',
  developer_ultimate: 'Developer Ultimate',
  free_trial: 'Community Edition',
  starter_plan: 'Community Edition',
  pay_as_you_go: 'Pay-as-you-go',
  enterprise_plan: 'Enterprise',
  corporate: 'Enterprise',
};

/** Human-readable plan title from dashboard slug (alias only — prefer DB plan_name) */
export function slugToDisplayName(slug) {
  if (!slug) return 'Subscription';
  return PLAN_SLUG_TO_DB_NAME[slug] || String(slug).replace(/_/g, ' ');
}

/**
 * ***** BH23072026_1300 — Do not invent USD prices from marketing catalog.
 * Price must come from API/DB (verify.price / plan.price). Always returns null.
 */
export function catalogPriceForSlug(_slug, _billingCycle = 'monthly') {
  return null;
}

const DEFAULT_SORT = 999;

/** Subscription price from API/DB — uses `price` column only (not cost_per_token). */
export function resolvePlanPriceFromDb(dbPlan) {
  if (dbPlan == null) return 0;
  const raw = dbPlan.price;
  if (raw === null || raw === undefined || raw === '') {
    return 0;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export function resolvePlanYearlyPriceFromDb(dbPlan) {
  if (dbPlan?.yearly_price != null && dbPlan.yearly_price !== '') {
    const y = Number(dbPlan.yearly_price);
    if (Number.isFinite(y) && y >= 0) return y;
  }
  // ***** BH23072026_1300 — no invented 20% yearly discount; monthly from DB only
  return null;
}

// ***** Code Start BH29072026_1745 - Yearly card price must never render "Custom"
// subscription_plans.yearly_price is NULL for every row, so the DB-only resolver above
// returned null and every paid plan showed "Custom /yr" on the Yearly tab (the Corporate
// treatment) even though the tab advertises a -20% discount off a known monthly price.
// Derive the same figure the marketing Pricing page already shows, from the one discount
// constant, so both surfaces agree. Checkout still refuses without a yearly Paddle price.
export function resolveYearlyDisplayPrice(dbPlan) {
  const fromDb = resolvePlanYearlyPriceFromDb(dbPlan);
  if (fromDb != null) return fromDb;

  const monthly = resolvePlanPriceFromDb(dbPlan);
  if (!(monthly > 0)) return null;

  const discount = Number(PRICING_HERO.yearlyDiscountPercent) || 0;
  return Math.round(monthly * 12 * (1 - discount / 100) * 100) / 100;
}
// ***** Code End BH29072026_1745

/**
 * Display fields from DB only (plan_name, description). No marketing catalog overrides.
 */
export function getPlanDisplayCopy(plan, billingInterval = 'monthly') {
  const dbName = String(plan?.name || plan?.plan_name || '').trim() || 'Plan';
  const dbDesc = String(plan?.description || plan?.short_description || '').trim();
  const { category } = classifyPlan(plan);

  let priceNote = dbDesc;
  if (!priceNote && billingInterval === 'yearly' && category === 'developer') {
    const yearly = resolveYearlyDisplayPrice(plan);
    if (yearly != null) {
      const perMonth = (Math.round((yearly / 12) * 100) / 100).toFixed(2);
      priceNote = `Billed yearly ($${perMonth}/mo equivalent). Auto-renews until canceled.`;
    }
  }

  return {
    displayName: dbName,
    tagline: dbDesc,
    priceNote,
    includesLabel: 'Includes:',
  };
}

export function mapPlanNameToSlug(planName) {
  if (!planName) return null;
  const name = String(planName).toLowerCase().trim();
  if (
    name.includes('community') ||
    name.includes('free') ||
    name.includes('trial') ||
    name.includes('starter')
  ) {
    return 'free_trial';
  }
  if (name.includes('developer ultimate') || (name.includes('ultimate') && name.includes('developer'))) {
    return 'developer_ultimate';
  }
  if (name.includes('developer plus') || (name.includes('plus') && name.includes('developer'))) {
    return 'developer_plus';
  }
  if (
    name.includes('professional') ||
    name.includes('developer basic') ||
    name.includes('developer') ||
    name === 'pro'
  ) {
    return 'pro_plan';
  }
  if (name.includes('pay') && (name.includes('go') || name.includes('you'))) return 'pay_as_you_go';
  if (name.includes('corporate') || name.includes('custom') || name.includes('enterprise')) {
    return 'corporate';
  }
  return name.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

// ***** Code Start BH25082026_1325 - Shared plan entitlement helpers (DB/API only; no invent)
function _parseOptInt(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.floor(n);
}

export function isPayAsYouGoPlan(planName, planType) {
  const type = String(planType || '').toLowerCase().trim();
  if (type === 'pay_as_you_go' || type === 'usage_based') return true;
  const name = String(planName || '').toLowerCase().replace(/_/g, ' ');
  return name.includes('pay') && name.includes('you') && name.includes('go');
}

/** Community / Starter / Free Trial free tier — never invent paid as free. */
export function isFreeLikePlan(planName, planType, isFreeTrialFlag) {
  if (isFreeTrialFlag === true) return true;
  if (isPayAsYouGoPlan(planName, planType)) return false;
  const type = String(planType || '').toLowerCase().trim();
  const name = String(planName || '').toLowerCase().trim().replace(/_/g, ' ');
  if (/(developer|corporate|enterprise|professional)/.test(name)) return false;
  if (type === 'free' || type === 'trial' || type === 'starter') return true;
  return (
    name.includes('community') ||
    name.includes('free trial') ||
    name === 'starter' ||
    name === 'free' ||
    name === 'trial'
  );
}

/** Display label when plan_name missing — prefer Community over "Free Trial". */
export function displayPlanName(planName, fallback = 'Community Edition') {
  const n = String(planName || '').trim();
  return n || fallback;
}

/**
 * Normalize entitlement ints from plans API or subscription status.
 * Does not invent defaults (null stays null).
 */
export function pickPlanEntitlements(source) {
  const s = source && typeof source === 'object' ? source : {};
  const presets = Array.isArray(s.allowed_model_presets)
    ? s.allowed_model_presets.map((x) => String(x || '').trim()).filter(Boolean)
    : null;
  const preset =
    (s.allowed_model_preset && String(s.allowed_model_preset).trim()) ||
    (presets && presets[0]) ||
    null;
  return {
    daily_token_limit: _parseOptInt(s.daily_token_limit),
    daily_tokens_used: _parseOptInt(s.daily_tokens_used),
    daily_tokens_remaining: _parseOptInt(s.daily_tokens_remaining),
    daily_prompt_limit: _parseOptInt(s.daily_prompt_limit),
    daily_prompts_used: _parseOptInt(s.daily_prompts_used),
    daily_prompts_remaining: _parseOptInt(s.daily_prompts_remaining),
    max_db_connections: _parseOptInt(s.max_db_connections),
    max_schemas: _parseOptInt(s.max_schemas),
    max_tables: _parseOptInt(s.max_tables),
    max_rows: _parseOptInt(s.max_rows),
    trial_days: _parseOptInt(s.trial_days),
    allowed_model_preset: preset,
    allowed_model_presets: presets || (preset ? [preset] : null),
  };
}

/** Rows for Plan limits UI: [label, value, hint] */
export function buildPlanLimitRows(source, opts = {}) {
  const e = pickPlanEntitlements(source);
  const freeLike = isFreeLikePlan(
    source?.plan_name || source?.name,
    source?.plan_type,
    source?.is_free_trial
  );
  const emptyLabel = opts.paidUnlimited && !freeLike ? 'Unlimited' : 'Not set';
  const fmt = (n) => (n == null ? emptyLabel : Number(n).toLocaleString('en-US'));
  const usedOf = (used, lim) => {
    if (lim == null) return emptyLabel;
    return `${Number(used || 0).toLocaleString('en-US')} / ${Number(lim).toLocaleString('en-US')}`;
  };
  return [
    {
      label: 'Daily tokens',
      value: usedOf(e.daily_tokens_used, e.daily_token_limit),
      hint: e.daily_token_limit != null ? 'Used today / daily cap' : emptyLabel,
    },
    {
      label: 'Daily prompts',
      value: usedOf(e.daily_prompts_used, e.daily_prompt_limit),
      hint: e.daily_prompt_limit != null ? 'Used today / daily cap' : emptyLabel,
    },
    { label: 'DB connections', value: fmt(e.max_db_connections), hint: 'Max saved connections' },
    { label: 'Schemas', value: fmt(e.max_schemas), hint: 'Max schemas' },
    { label: 'Tables', value: fmt(e.max_tables), hint: 'Max tables' },
    { label: 'Rows', value: fmt(e.max_rows), hint: 'Max rows' },
    {
      label: 'Model',
      value: e.allowed_model_preset || (freeLike ? 'Plan model' : 'Any allowed'),
      hint: 'Allowed model preset',
    },
  ];
}

/** Token reset badge copy — daily vs monthly vs PAYG. */
export function tokenResetLabel(subscription) {
  if (isPayAsYouGoPlan(subscription?.plan_name, subscription?.plan_type)) {
    return 'Pay-as-you-go balance';
  }
  if (
    subscription?.daily_token_limit != null ||
    subscription?.daily_prompt_limit != null
  ) {
    return 'Daily + monthly caps';
  }
  return 'Resets monthly';
}
// ***** Code End BH25082026_1325

export function parseDbPlanId(rawId) {
  if (rawId == null || rawId === '') return null;
  const s = String(rawId);
  if (s.startsWith('plan_')) return s.slice(5);
  return s;
}

export function classifyPlan(plan) {
  const planType = (plan?.plan_type || '').toLowerCase();
  if (planType === 'addon' || planType === 'add-on') {
    return { category: 'addon', tier: null };
  }
  const name = (plan?.name || plan?.plan_name || '').toLowerCase();
  if (planType === 'trial' || planType === 'starter' || planType === 'free') {
    return { category: 'starter', tier: null };
  }
  if (planType === 'usage_based' || planType === 'payg') {
    return { category: 'payg', tier: null };
  }
  if (planType === 'enterprise' || planType === 'corporate') {
    return { category: 'enterprise', tier: null };
  }
  if (
    planType === 'developer' ||
    planType === 'subscription' ||
    planType === 'monthly'
  ) {
    if (name.includes('ultimate')) return { category: 'developer', tier: 'ultimate' };
    if (name.includes('plus')) return { category: 'developer', tier: 'plus' };
    return { category: 'developer', tier: 'basic' };
  }

  if (name.includes('add-on') || name.includes('addon')) return { category: 'addon', tier: null };
  if (name.includes('free') || name.includes('trial') || name.includes('starter') || name.includes('community')) {
    return { category: 'starter', tier: null };
  }
  if (name.includes('pay') && (name.includes('go') || name.includes('you'))) {
    return { category: 'payg', tier: null };
  }
  if (name.includes('corporate') || name.includes('custom') || name.includes('enterprise')) {
    return { category: 'enterprise', tier: null };
  }
  if (
    name.includes('professional') ||
    name.includes('developer') ||
    name.includes('pro')
  ) {
    if (name.includes('ultimate')) return { category: 'developer', tier: 'ultimate' };
    if (name.includes('plus')) return { category: 'developer', tier: 'plus' };
    return { category: 'developer', tier: 'basic' };
  }
  return { category: 'other', tier: null };
}

export function isPlanActive(plan) {
  return plan && plan.is_active !== false && plan.is_active !== 0;
}

export function filterActivePlans(plans) {
  if (!Array.isArray(plans)) return [];
  return plans.filter((p) => {
    if (!isPlanActive(p)) return false;
    return classifyPlan(p).category !== 'addon';
  });
}

export function comparePlansBySort(a, b) {
  const sa = Number(a?.plan_sort ?? DEFAULT_SORT);
  const sb = Number(b?.plan_sort ?? DEFAULT_SORT);
  if (sa !== sb) return sa - sb;
  const pa = Number(a?.price ?? 0);
  const pb = Number(b?.price ?? 0);
  if (pa !== pb) return pa - pb;
  return String(a?.name || '').localeCompare(String(b?.name || ''));
}

export function sortPlansByOrder(plans) {
  return [...filterActivePlans(plans)].sort(comparePlansBySort);
}

export function groupPlansForPricingGrid(plans) {
  const allSorted = sortPlansByOrder(plans);
  const starter = allSorted.find((p) => classifyPlan(p).category === 'starter') || null;
  const developerTiers = allSorted.filter((p) => classifyPlan(p).category === 'developer');
  const payg = allSorted.find((p) => classifyPlan(p).category === 'payg') || null;
  const enterprise =
    allSorted.find((p) => classifyPlan(p).category === 'enterprise') || null;
  const other = allSorted.filter((p) => {
    const c = classifyPlan(p).category;
    return c !== 'starter' && c !== 'developer' && c !== 'payg' && c !== 'enterprise';
  });
  return { starter, developerTiers, payg, enterprise, other, allSorted };
}

export function resolveCurrentPlanSlug(subscription, plansList) {
  if (!subscription) return null;
  const status = subscription.status?.toLowerCase();
  if (status === 'no_subscription' || (!subscription.plan_name && status !== 'expired')) {
    return null;
  }
  if (status === 'expired') return null;
  if (status === 'cancelled' && subscription.end_date) {
    try {
      if (new Date(subscription.end_date) < new Date()) return null;
    } catch (e) {
      /* ignore */
    }
  }

  const subPlanId = subscription.plan_id != null ? String(subscription.plan_id) : null;
  const planName = (subscription.plan_name || '').toLowerCase();

  if (subPlanId && Array.isArray(plansList)) {
    const byId = plansList.find((p) => {
      const dbId = p.db_id != null ? String(p.db_id) : null;
      return dbId === subPlanId || String(p.id) === subPlanId;
    });
    if (byId?.slug) return byId.slug;
  }

  if (Array.isArray(plansList)) {
    for (const p of plansList) {
      const n = (p.name || '').toLowerCase();
      if (planName && n && (planName === n || planName.includes(n) || n.includes(planName))) {
        return p.slug;
      }
    }
  }

  return mapPlanNameToSlug(subscription.plan_name);
}

export function formatMoney(amount, { prefix = '$', decimals = 2 } = {}) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 'Custom';
  if (n === 0) return '$0';
  return `${prefix}${n.toFixed(decimals)}`;
}

export function formatPlanPriceDisplay(plan, billingInterval = 'monthly') {
  const price = resolvePlanPriceFromDb(plan);
  const { category } = classifyPlan(plan);
  const copy = getPlanDisplayCopy(plan, billingInterval);

  if (category === 'enterprise' || (price <= 0 && category !== 'starter' && category !== 'payg')) {
    return {
      amount: 'Custom',
      suffix: null,
      note: copy.priceNote,
    };
  }

  if (category === 'starter' && price <= 0) {
    return {
      amount: '$0',
      suffix: '/forever',
      note: copy.priceNote,
    };
  }

  if (category === 'payg') {
    return {
      amount: price > 0 ? formatMoney(price) : 'Custom',
      suffix: price > 0 ? 'initial' : null,
      note: copy.priceNote,
    };
  }

  if (billingInterval === 'yearly') {
    const yearly = resolveYearlyDisplayPrice(plan);
    return {
      amount: yearly != null ? formatMoney(yearly) : 'Custom',
      suffix: '/yr',
      note: copy.priceNote,
    };
  }

  return {
    amount: price > 0 ? formatMoney(price) : '$0',
    suffix: '/mo',
    note: copy.priceNote,
  };
}

export function getCheckoutPrice(plan, billingInterval = 'monthly') {
  const price = resolvePlanPriceFromDb(plan);
  if (billingInterval !== 'yearly') {
    return { price, interval: 'month' };
  }
  const yearly = resolveYearlyDisplayPrice(plan);
  if (yearly != null) {
    return { price: yearly, interval: 'year' };
  }
  return { price, interval: 'year' };
}

export function developerTierKey(plan) {
  const { tier } = classifyPlan(plan);
  return tier || 'basic';
}

export function tierBadgeLabel(tier) {
  if (tier === 'plus') return 'Advanced';
  if (tier === 'ultimate') return 'Ultimate';
  return 'Popular';
}
